import React from "react";
import { assetUrl } from "../../lib/api";

// Clip-paths for shaped image/player elements (circle handled separately).
export const SHAPE_CLIP = {
    square: undefined,
    diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
    hexagon: "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
};

const maskStyle = (url) => ({
    backgroundImage: `url(${url})`, backgroundSize: "cover", backgroundPosition: "center",
    WebkitBackgroundClip: "text", backgroundClip: "text", WebkitTextFillColor: "transparent", color: "transparent",
});

// Shapes/photos are 1:1 (width-driven) so a "circle" is a REAL circle on any canvas.
const isSquareKind = (el) => el.kind === "ellipse" || el.kind === "player" || (el.kind === "image" && el.shape && el.shape !== "free");

function ElementView({ el }) {
    const base = {
        position: "absolute",
        left: `${el.xPct ?? 10}%`, top: `${el.yPct ?? 10}%`,
        width: `${el.wPct ?? 22}%`,
        opacity: el.opacity ?? 1,
        transform: `rotate(${el.rot || 0}deg)`,
        ...(isSquareKind(el) ? { aspectRatio: "1 / 1" } : { height: `${el.hPct ?? 22}%` }),
    };
    if (el.kind === "rect") return <div style={{ ...base, background: el.color || "#9D4CDD" }} />;
    if (el.kind === "ellipse") return <div style={{ ...base, background: el.color || "#9D4CDD", clipPath: "circle(50%)" }} />;
    if (el.kind === "line") return <div style={{ position: "absolute", left: `${el.xPct ?? 10}%`, top: `${el.yPct ?? 10}%`, width: `${el.wPct ?? 40}%`, height: `${el.thick ?? 4}px`, background: el.color || "#FFFFFF", opacity: el.opacity ?? 1, transform: `rotate(${el.rot || 0}deg)` }} />;
    if (el.kind === "text") {
        const fill = el.fillImage ? maskStyle(el.fillImage) : { color: el.color || "#FFFFFF" };
        return (
            <div style={{ ...base, height: `${el.hPct ?? 16}%`, aspectRatio: undefined, display: "flex", alignItems: "center", justifyContent: el.align === "center" ? "center" : el.align === "right" ? "flex-end" : "flex-start" }}>
                <div className="font-story-punch uppercase leading-none whitespace-pre-wrap" style={{ fontSize: `${el.fontPct ?? 9}cqw`, letterSpacing: `${el.tracking ?? 0}em`, ...fill }}>{el.text || "TEXT"}</div>
            </div>
        );
    }
    if (el.kind === "image" || el.kind === "player") {
        const src = el.image ? (el.image.startsWith("data:") || el.image.startsWith("http") ? el.image : assetUrl(el.image)) : null;
        const clip = el.shape === "circle" ? "circle(50%)" : SHAPE_CLIP[el.shape];
        return (
            <div style={{ ...base, overflow: "hidden", clipPath: clip, background: "rgba(255,255,255,0.05)" }}>
                {src ? <img src={src} alt="" className="w-full h-full object-cover object-top" loading="lazy" /> : <div className="w-full h-full grid place-items-center text-[10px] text-white/40">image</div>}
            </div>
        );
    }
    return null;
}

// Overlay of user-added elements — inside the export node so it renders everywhere.
export function ElementsOverlay({ els }) {
    if (!els || !els.length) return null;
    return (
        <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" style={{ containerType: "size" }} data-els-overlay>
            {els.map((el) => el.hidden ? null : <ElementView key={el.id} el={el} />)}
        </div>
    );
}

let _seq = 0;
export const newElement = (kind, extra = {}) => {
    _seq += 1; const id = `el_${Date.now().toString(36)}_${_seq}`;
    const common = { id, kind, xPct: 34, yPct: 34, wPct: 28, hPct: 28, rot: 0, opacity: 1 };
    if (kind === "text") return { ...common, wPct: 50, hPct: 16, text: "TEXT", color: "#FFFFFF", fontPct: 9, align: "left", tracking: 0, ...extra };
    if (kind === "rect") return { ...common, color: "#9D4CDD", ...extra };
    if (kind === "ellipse") return { ...common, color: "#9D4CDD", ...extra };
    if (kind === "line") return { ...common, wPct: 40, thick: 4, color: "#FFFFFF", ...extra };
    if (kind === "image") return { ...common, image: "", shape: "square", ...extra };
    if (kind === "player") return { ...common, kind: "player", image: "", shape: "circle", ...extra };
    return common;
};

export const ELEMENT_LABEL = { text: "Text", rect: "Rectangle", ellipse: "Circle", line: "Line", image: "Image", player: "Player" };
